import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, InputNumber, message, Typography, Popconfirm, Popover, Modal, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, EditOutlined, SearchOutlined, MoreOutlined, QuestionCircleOutlined, InfoCircleOutlined, StarFilled, StarOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import '../css/searchComponent.css';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const OrganizerPlayers = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] });
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchOperation, setBatchOperation] = useState(null);
  const [batchValue, setBatchValue] = useState(null);
  const [batchPopoverVisible, setBatchPopoverVisible] = useState(false);
  const [exporting, setExporting] = useState(false);

  const POSITION_LABEL = { 1: 'Jogador de linha', 2: 'Goleiro' };
  const TYPE_LABEL = { 1: 'Mensalista', 2: 'Avulso' };
  const LEVEL_LABEL = {
    1: 'Zero noção', 2: 'Muito fraco', 3: 'Fraco', 4: 'Razoável básico', 5: 'Jogador comum',
    6: 'Intermediário', 7: 'Bom de bola', 8: 'Bola no pé', 9: 'Quase craque', 10: 'Nível profissional',
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/organizer-players/export', { params: { search: searchText } });
      const rows = r.data?.data || [];
      if (rows.length === 0) {
        message.warning('Nenhum jogador pra exportar com esse filtro');
        return;
      }
      const sheetRows = rows.map(p => ({
        Nome: p.name || '',
        Telefone: p.phone || '',
        Posição: POSITION_LABEL[p.position] || p.position || '',
        Tipo: TYPE_LABEL[p.type] || p.type || '',
        'Nível técnico': p.technical_level ? `${p.technical_level} — ${LEVEL_LABEL[p.technical_level] || ''}` : '',
        Status: p.status || '',
        'Cadastrado em': p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '',
      }));
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      const cols = Object.keys(sheetRows[0]);
      ws['!cols'] = cols.map(k => ({
        wch: Math.min(60, Math.max(k.length, ...sheetRows.map(r => String(r[k] || '').length)) + 2),
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Jogadores');
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = searchText
        ? `jogadores_${stamp}_filtro-${searchText.replace(/[^\w]+/g, '-')}.xlsx`
        : `jogadores_${stamp}.xlsx`;
      XLSX.writeFile(wb, fname);
      message.success(`${rows.length} jogador(es) exportado(s)${r.data?.capped ? ' (limite 5k)' : ''}`);
    } catch (err) {
      console.error('[export-players]', err, err?.response?.data, err?.response?.status);
      const httpStatus = err?.response?.status;
      const backendMsg = err?.response?.data?.message;
      const detail = backendMsg
        ? `${backendMsg} (HTTP ${httpStatus})`
        : httpStatus
          ? `HTTP ${httpStatus}${err?.message ? ` — ${err.message}` : ''}`
          : (err?.message || 'erro desconhecido — veja Console (F12)');
      message.error(`Erro ao exportar: ${detail}`);
    } finally {
      setExporting(false);
    }
  };

  const fetchPlayers = async (page = 1, search = '', pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get('/organizer-players', {
        params: { page, limit: pageSize, search }
      });
      setPlayers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, current: page, pageSize }));
    } catch (error) {
      message.error('Erro ao carregar jogadores');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  const handleTableChange = (pag) => {
    const newPage = pag.pageSize !== pagination.pageSize ? 1 : pag.current;
    fetchPlayers(newPage, searchText, pag.pageSize);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchPlayers(1, value, pagination.pageSize);
  };

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (id) => {
    try {
      const row = players.find(item => item.id === id);
      await api.put(`/organizer-players/${id}`, row);
      setEditingKey('');
      message.success('Jogador atualizado com sucesso!');
      fetchPlayers(pagination.current, searchText, pagination.pageSize);
    } catch (error) {
      message.error('Erro ao salvar jogador');
    }
  };

  const handleAdd = async () => {
    const newPlayer = {
      name: '',
      phone: '',
      position: '1',
      status: 'ATIVO',
      technical_level: 1,
      type: '1'
    };

    try {
      const response = await api.post('/organizer-players', newPlayer);
      message.success('Jogador adicionado com sucesso!');
      // Ir para primeira página e recarregar
      await fetchPlayers(1, searchText, pagination.pageSize);
      // Colocar o novo jogador em modo edição
      setEditingKey(response.data.id);
    } catch (error) {
      message.error('Erro ao adicionar jogador');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.patch(`/organizer-players/delete/${id}`);
      message.success('Jogador foi marcado como excluído com sucesso!');
      fetchPlayers(pagination.current, searchText, pagination.pageSize);
    } catch (error) {
      message.error('Erro ao marcar jogador como excluído');
    }
  };

  const handleInputChange = (value, id, field) => {
    const newPlayers = players.map(player => {
      if (player.id === id) {
        return { ...player, [field]: value };
      }
      return player;
    });
    setPlayers(newPlayers);
  };

  const isMobile = window.innerWidth <= 768;
  const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';

  const renderMobileActions = (record) => {
    const editing = isEditing(record);

    if (editing) {
      return (
        <Button
          icon={<SaveOutlined />}
          onClick={() => save(record.id)}
          type="primary"
          size="small"
        />
      );
    }

    const menuContent = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button
          icon={<EditOutlined />}
          onClick={() => edit(record)}
          size="small"
          block
        >
          Editar
        </Button>
        <Popconfirm
          title="Marcar este jogador como excluído?"
          onConfirm={() => handleDelete(record.id)}
          placement="top"
        >
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            block
          >
            Excluir
          </Button>
        </Popconfirm>
      </div>
    );

    return (
      <Popover
        content={menuContent}
        trigger="click"
        placement="bottomRight"
      >
        <Button
          icon={<MoreOutlined />}
          size="small"
        />
      </Popover>
    );
  };

  const renderDesktopActions = (record) => {
    const editing = isEditing(record);
    return editing ? (
      <span>
        <Button
          icon={<SaveOutlined />}
          onClick={() => save(record.id)}
          size="small"
          style={{ marginRight: 8 }}
        />
        <Button onClick={cancel} size="small">
          Cancelar
        </Button>
      </span>
    ) : (
      <span>
        <Button
          icon={<EditOutlined />}
          onClick={() => edit(record)}
          size="small"
          style={{ marginRight: 8 }}
        />
        <Popconfirm
          title="Marcar este jogador como excluído?"
          onConfirm={() => handleDelete(record.id)}
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      </span>
    );
  };

  const actionsColumn = {
    title: isMobile ? '' : 'Ações',
    key: 'actions',
    width: isMobile ? 50 : 120,
    fixed: isMobile ? 'left' : false,
    render: (_, record) => {
      return isMobile ? renderMobileActions(record) : renderDesktopActions(record);
    }
  };

  const dataColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Input
            value={text}
            onChange={(e) => handleInputChange(e.target.value, record.id, 'name')}
          />
        ) : text;
      }
    },
    {
      title: 'Telefone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Input
            value={text}
            onChange={(e) => handleInputChange(e.target.value, record.id, 'phone')}
          />
        ) : text;
      }
    },
    {
      title: 'Posição',
      dataIndex: 'position',
      key: 'position',
      render: (text, record) => {
        const editing = isEditing(record);
        const displayText = text === '1' || text === 1 ? 'Jogador de Linha' : 'Goleiro';
        return editing ? (
          <Select
            value={text === '1' || text === 1 ? '1' : '2'}
            onChange={(value) => handleInputChange(value, record.id, 'position')}
            style={{ width: '100%' }}
          >
            <Option value="1">Jogador de Linha</Option>
            <Option value="2">Goleiro</Option>
          </Select>
        ) : displayText;
      }
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => {
        const editing = isEditing(record);
        const displayText = text === '1' || text === 1 ? 'Mensalista' : 'Avulso';
        return editing ? (
          <Select
            value={text === '1' || text === 1 ? '1' : '2'}
            onChange={(value) => handleInputChange(value, record.id, 'type')}
            style={{ width: '100%' }}
          >
            <Option value="1">Mensalista</Option>
            <Option value="2">Avulso</Option>
          </Select>
        ) : displayText;
      }
        },
        {
      title: (
        <div>
          Status
          <Tooltip
            title={
              <div>
                <div><strong>Ativo:</strong> Jogador disponível para convocações.</div>
                <div style={{ marginTop: 6 }}><strong>Inativo:</strong> Jogador temporariamente inativo, porém, continua recebendo mensagens de convocações.</div>
                <div style={{ marginTop: 6 }}><strong>Suspenso:</strong> Jogador temporariamente suspenso, não recebe mensagens de convocações e só pode ser reativado manualmente.</div>
                <div style={{ marginTop: 6 }}><strong>Afastado:</strong> Jogador afastado indefinidamente, porém, continua recebendo mensagens de convocações.</div>
              </div>
            }
            >
            <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff', cursor: 'help' }} />
          </Tooltip>
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const editing = isEditing(record);
        return editing ? (
          <Select
            value={text}
            onChange={(value) => handleInputChange(value, record.id, 'status')}
            style={{ width: '100%' }}
          >
            <Option value="ATIVO">Ativo</Option>
            <Option value="INATIVO">Inativo</Option>
            <Option value="SUSPENSO">Suspenso</Option>
            <Option value="AFASTADO">Afastado</Option>
          </Select>
        ) : text;
      }
    },
    {
      title: 'Nível Técnico',
      dataIndex: 'technical_level',
      key: 'technical_level',
      render: (text, record) => {
        const editing = isEditing(record);
        return editing ? (
          <InputNumber
            value={text}
            min={1}
            max={10}
            onChange={(value) => handleInputChange(value, record.id, 'technical_level')}
            style={{ width: '100%' }}
          />
        ) : text;
      }
    },
    {
      title: (
        <Tooltip title="Jogador cadastrado no app da comunidade pode dar e receber avaliações depois dos jogos.">
          <span>Avalia? <InfoCircleOutlined style={{ color: '#8c8c8c' }} /></span>
        </Tooltip>
      ),
      dataIndex: 'has_community_player',
      key: 'has_community_player',
      align: 'center',
      width: 90,
      render: (v) => v
        ? <Tooltip title="Cadastrado no app comunidade — pode dar e receber avaliações"><StarFilled style={{ color: '#faad14', fontSize: 16 }} /></Tooltip>
        : <Tooltip title="Ainda não usa o app comunidade — não aparece nas avaliações"><StarOutlined style={{ color: '#bfbfbf', fontSize: 16 }} /></Tooltip>,
    }
  ];

  const handleBatchSave = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Selecione pelo menos um jogador');
      return;
    }

    const selectedPlayers = players.filter(p => selectedRowKeys.includes(p.id));
    const playerNames = selectedPlayers.map(p => p.name).join(', ');

    Modal.confirm({
      title: `Confirma a alteração de "${batchOperation}" para os seguintes jogadores?`,
      content: playerNames,
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map(id =>
              api.put(`/organizer-players/${id}`, {
                ...players.find(p => p.id === id),
                [batchOperation === 'Tipo' ? 'type' :
                 batchOperation === 'Posição' ? 'position' : 'technical_level']: batchValue
              })
            )
          );
          message.success('Jogadores atualizados com sucesso!');
          setBatchOperation(null);
          setBatchValue(null);
          setSelectedRowKeys([]);
          fetchPlayers(pagination.current, searchText, pagination.pageSize);
        } catch (error) {
          message.error('Erro ao atualizar jogadores');
        }
      }
    });
  };

  const handleBatchOperationSelect = (operation) => {
    setBatchOperation(operation);
    setBatchPopoverVisible(false);
  };

  const batchMenuContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 150 }}>
      <Button size="small" block onClick={() => handleBatchOperationSelect('Tipo')}>Tipo</Button>
      <Button size="small" block onClick={() => handleBatchOperationSelect('Posição')}>Posição</Button>
      <Button size="small" block onClick={() => handleBatchOperationSelect('Nível Técnico')}>Nível Técnico</Button>
    </div>
  );

  const renderBatchForm = () => {
    if (!batchOperation) return null;

    return (
      <div style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
        <div style={{ marginBottom: 8 }}>
          <strong>Alteração em lote - {batchOperation}</strong>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {batchOperation === 'Tipo' && (
            <Select
              placeholder="Selecione o tipo"
              value={batchValue}
              onChange={setBatchValue}
              style={{ width: 150 }}
            >
              <Option value="1">Mensalista</Option>
              <Option value="2">Avulso</Option>
            </Select>
          )}
          {batchOperation === 'Posição' && (
            <Select
              placeholder="Selecione a posição"
              value={batchValue}
              onChange={setBatchValue}
              style={{ width: 150 }}
            >
              <Option value="1">Jogador de Linha</Option>
              <Option value="2">Goleiro</Option>
            </Select>
          )}
          {batchOperation === 'Nível Técnico' && (
            <InputNumber
              placeholder="Nível (1-10)"
              value={batchValue}
              onChange={setBatchValue}
              min={1}
              max={10}
              style={{ width: 150 }}
            />
          )}
          <Button type="primary" onClick={handleBatchSave}>Salvar</Button>
          <Button onClick={() => { setBatchOperation(null); setBatchValue(null); }}>Cancelar</Button>
        </div>
      </div>
    );
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const columns = isMobile ? [actionsColumn, ...dataColumns] : [...dataColumns, actionsColumn];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Title level={2} style={{ margin: 0 }}>Jogadores ({pagination.total})</Title>
          <Popover
            title="Qual tipo de jogador usar?"
            content={
              <div style={{ maxWidth: 300 }}>
                <p style={{ marginBottom: 8 }}>
                  O conceito de <strong>mensalista</strong> e <strong>avulso</strong> é exclusivo para definir quem receberá convocação prioritária.
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>Mensalistas:</strong> Recebem a primeira convocação 24h antes dos avulsos.
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>Avulsos:</strong> Recebem da segunda convocação em diante.
                </p>
                <p style={{ marginBottom: 8 }}>
                  <strong>Dica:</strong> Se seu time não tem mensalistas e você quer que todos recebam desde a primeira convocação, adicione todos como mensalistas.
                </p>
                <p style={{ margin: 0 }}>
                  Esta informação também é utilizada no módulo de cobrança para cobrar mensalidade de quem é mensalista.
                </p>
              </div>
            }
            trigger="click"
            placement={isMobile ? 'bottom' : 'bottomLeft'}
          >
            <Button
              icon={<QuestionCircleOutlined />}
              type="text"
              size="small"
              style={{ color: '#1890ff' }}
              title="Ajuda sobre tipos de jogadores"
            />
          </Popover>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExport}
            loading={exporting}
            title={searchText ? `Exporta todos os jogadores que casam com "${searchText}"` : 'Exporta todos os jogadores do time'}
          >
            {isMobile ? 'XLS' : 'Exportar XLS'}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {isMobile ? 'Adicionar' : 'Adicionar Jogador'}
          </Button>
        </div>
      </div>

      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 8,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Popover
            content={batchMenuContent}
            trigger="click"
            placement="bottomLeft"
            open={batchPopoverVisible}
            onOpenChange={setBatchPopoverVisible}
          >
            <Button icon={<MoreOutlined />} title="Operações em lote" size={isMobile ? 'large' : 'middle'} />
          </Popover>
          <Search
            className={mobileSearchClass}
            style={{ width: isMobile ? "100%" : "300px" }}
            placeholder="Buscar por nome ou telefone..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
          />
        </div>
      </div>

      {renderBatchForm()}

      <Table
        columns={columns}
        dataSource={players}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        showSorterTooltip={false}
        rowSelection={rowSelection}
      />
    </div>
  );
};

export default OrganizerPlayers;